import { useNavigate } from "@tanstack/react-router";
import type { MerchantDraftData } from "@unbox/api";
import { AlertCircle, ArrowLeft } from "@untitledui/icons";
import { type ReactNode, useEffect, useRef, useState } from "react";
import {
  type MerchantDraftItem,
  type UpdateMerchantDraftInput,
  useChooseDraftShop,
  useDiscardMerchantDraft,
  useRefreshMerchantDraft,
  useSaveMerchantDraftForApproval,
  useSubmitMerchantDraft,
  useUpdateMerchantDraft,
} from "@/api/workspace-merchants";
import { useWorkspaceRiskCatalog, useWorkspaceRisksList } from "@/api/workspace-risks";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";
import { startingRisks } from "./risk-setup";
import { type HandlingForm, resolvedHandling, StepConfigureRisks } from "./step-configure-risks";
import {
  type CredentialGroup,
  connectFooterMessage,
  isTikTokConnected,
  StepConnectTikTok,
} from "./step-connect-tiktok";
import { isDetailsComplete, StepDetails, useDetailsForm } from "./step-details";
import { countSelectedRisks, type DetectionValue, StepDetectRisks } from "./step-detect-risks";
import { StepReview } from "./step-review";
import { StepHeading, WIZARD_STEPS, WizardProgress } from "./wizard-ui";

const HEADINGS: Record<number, { title: string; description: string }> = {
  1: {
    title: "Add merchant details",
    description: "Enter the merchant's details and tell us how their brand should appear in Unbox.",
  },
  2: {
    title: "What risks would you like to detect?",
    description:
      "Tell us which risks you want Unbox to watch for across your creator content. You can always update these settings later in the app.",
  },
  3: {
    title: "Configure risks",
    description:
      "Set the language creators hear from you, then decide what happens when a risk is found. You can change all of this later in the app.",
  },
  4: {
    title: "Connect TikTok shop",
    description: "Choose where your shop is registered to get the right TikTok login.",
  },
  5: {
    title: "Review & submit",
    description: "Check everything below before you finish. You can jump back to any step to make changes.",
  },
};

type FooterMessage = { text: string; isWarning: boolean };

export const AddMerchantWizard = ({ draft, workspaceId }: { draft: MerchantDraftItem; workspaceId: string }) => {
  const navigate = useNavigate();
  const { data: catalog } = useWorkspaceRiskCatalog(workspaceId);
  const { data: workspaceRisks } = useWorkspaceRisksList(workspaceId);
  const updateDraft = useUpdateMerchantDraft();
  const chooseShop = useChooseDraftShop();
  const discardDraft = useDiscardMerchantDraft();
  const refreshDraft = useRefreshMerchantDraft();
  const submitDraft = useSubmitMerchantDraft();
  const saveForApproval = useSaveMerchantDraftForApproval();
  const connected = isTikTokConnected(draft);
  const scrollArea = useRef<HTMLDivElement>(null);

  const [step, setStep] = useState(draft.step);
  const [showErrors, setShowErrors] = useState(false);
  const [credentialGroup, setCredentialGroup] = useState<CredentialGroup | null>(draft.credentialGroup);
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);
  const [connectingShop, setConnectingShop] = useState(false);
  const chosenShopId =
    !isTikTokConnected(draft) && draft.shopCandidates.some((shop) => shop.shopId === selectedShopId)
      ? selectedShopId
      : null;
  const detailsForm = useDetailsForm(draft.data.details ?? { handle: "", name: "" });
  const details = detailsForm.watch();
  const [detection, setDetection] = useState<DetectionValue | null>(draft.data.detection ?? null);
  const [handling, setHandling] = useState<HandlingForm>({
    language: draft.data.handling?.language ?? "en",
    mode: draft.data.handling?.mode ?? null,
    risks: draft.data.handling?.risks ?? [],
  });

  const riskDataReady = !!catalog && !!workspaceRisks;
  const hasWorkspaceSafety = (workspaceRisks?.length ?? 0) > 0;

  useEffect(() => {
    if (step >= 2 && riskDataReady && !hasWorkspaceSafety && !detection) {
      setDetection({ startingPoint: "defaults", risks: startingRisks(catalog, workspaceRisks, "defaults") });
    }
  }, [step, riskDataReady, hasWorkspaceSafety, detection, catalog, workspaceRisks]);

  useEffect(() => {
    setShowErrors(false);
  }, [step, details.handle, details.name, detection, handling.mode, credentialGroup]);

  const isStepValid = (n: number) => {
    if (n === 1) return isDetailsComplete(details);
    if (n === 2) return !!detection;
    if (n === 3) return !!handling.mode;
    if (n === 4) return !!credentialGroup;
    return true;
  };

  const footerMessage = (): FooterMessage | null => {
    if (step === 1)
      return isStepValid(1) ? null : { text: "Enter a handle and a merchant name to continue", isWarning: showErrors };
    if (step === 2) {
      if (!detection) return { text: "Choose a starting point to begin", isWarning: showErrors };
      const enabled = catalog ? countSelectedRisks(catalog, detection.risks) : detection.risks.length;
      return { text: `${enabled} risks enabled. Select more or continue.`, isWarning: false };
    }
    if (step === 3) {
      if (!handling.mode) return { text: "Choose how to handle detected risks", isWarning: showErrors };
      if (handling.mode === "skip") return { text: "Every risk will be sent for manual review", isWarning: false };
      return { text: "Set what happens for each risk, then continue", isWarning: false };
    }
    if (step === 4)
      return {
        text: connectFooterMessage(draft, credentialGroup, connectingShop),
        isWarning: showErrors && !credentialGroup,
      };
    if (step === 5) {
      return connected
        ? { text: "Review everything, then submit.", isWarning: false }
        : {
            text: "Save your progress. Setup completes once someone approves access in TikTok Shop.",
            isWarning: false,
          };
    }
    return null;
  };

  const draftData = (): UpdateMerchantDraftInput["data"] => {
    const data: NonNullable<UpdateMerchantDraftInput["data"]> = {
      details: { handle: details.handle.trim(), name: details.name.trim() },
    };
    if (detection) data.detection = detection;
    if (handling.mode && detection && catalog && workspaceRisks) {
      const handlingData: NonNullable<MerchantDraftData["handling"]> = {
        language: handling.language,
        mode: handling.mode,
        risks: resolvedHandling(catalog, workspaceRisks, detection.risks, handling),
      };
      data.handling = handlingData;
    }
    return data;
  };

  const save = async (nextStep: number, onSuccess: () => void) => {
    let version = draft.version;
    if (chosenShopId) {
      try {
        const chosen = await chooseShop.mutateAsync(chosenShopId);
        if (!chosen) {
          backToMerchants();
          return;
        }
        version = chosen.version;
      } catch {
        return;
      }
    }
    updateDraft.mutate(
      { version, step: nextStep, credentialGroup, data: draftData() },
      { onSuccess: () => onSuccess() },
    );
  };

  const isSaving = updateDraft.isPending || chooseShop.isPending;

  const savingCredentialGroup = useRef<Promise<unknown> | null>(null);
  const saveCredentialGroup = async () => {
    if (!credentialGroup || credentialGroup === draft.credentialGroup) return;
    savingCredentialGroup.current ??= updateDraft
      .mutateAsync({ version: draft.version, credentialGroup })
      .finally(() => {
        savingCredentialGroup.current = null;
      });
    await savingCredentialGroup.current;
  };

  const goTo = (next: number) => {
    setStep(next);
    scrollArea.current?.scrollTo({ top: 0 });
  };

  const backToMerchants = () => navigate({ to: "/workspace/$workspaceId/settings/merchants", params: { workspaceId } });

  const onFinish = () => {
    if (connected) {
      submitDraft.mutate(draft.version, {
        onSuccess: async (merchant) => {
          await navigate({
            to: "/workspace/$workspaceId/settings/merchants",
            params: { workspaceId },
            search: { added: merchant.name },
          });
          await refreshDraft();
        },
      });
      return;
    }
    saveForApproval.mutate(draft.version, { onSuccess: backToMerchants });
  };

  const onContinue = async () => {
    if (step === WIZARD_STEPS.length) {
      onFinish();
      return;
    }
    if (step === 1 && !(await detailsForm.trigger())) {
      setShowErrors(true);
      return;
    }
    if (!isStepValid(step)) {
      setShowErrors(true);
      return;
    }
    const next = Math.min(step + 1, WIZARD_STEPS.length);
    save(Math.max(next, draft.step), () => goTo(next));
  };

  const onSaveAndExit = () => save(Math.max(step, draft.step), backToMerchants);

  const onDiscard = () => discardDraft.mutate(undefined, { onSuccess: backToMerchants });

  const furthest = Math.max(draft.step, step);
  const finishLabel = connected ? "Add merchant" : "Save";
  const primaryLabel = step === WIZARD_STEPS.length ? finishLabel : "Continue";
  const message = footerMessage();
  const heading =
    step === 4 && credentialGroup
      ? {
          ...HEADINGS[4],
          description:
            "Log in to TikTok Shop with the shop owner's login and approve access for Unbox. TikTok opens in a pop-up.",
        }
      : HEADINGS[step];

  const stepContent = (): ReactNode => {
    if (step === 1) return <StepDetails form={detailsForm} draft={draft} />;
    if (!riskDataReady) return <p className="text-sm text-tertiary">Loading risks…</p>;
    if (step === 2) {
      return (
        <StepDetectRisks
          catalog={catalog}
          workspaceRisks={workspaceRisks}
          value={detection}
          onChange={setDetection}
          showErrors={showErrors}
        />
      );
    }
    if (step === 3) {
      return (
        <StepConfigureRisks
          catalog={catalog}
          workspaceRisks={workspaceRisks}
          selection={detection?.risks ?? []}
          value={handling}
          onChange={setHandling}
          showErrors={showErrors}
        />
      );
    }
    if (step === 5) {
      return (
        <StepReview
          draft={draft}
          details={details}
          detection={detection}
          handling={handling}
          credentialGroup={credentialGroup}
          catalog={catalog}
          onEdit={goTo}
        />
      );
    }
    if (step === 4) {
      return (
        <StepConnectTikTok
          draft={draft}
          credentialGroup={credentialGroup}
          onCredentialGroupChange={setCredentialGroup}
          onSaveCredentialGroup={saveCredentialGroup}
          showErrors={showErrors}
          onConnectionChange={refreshDraft}
          chosenShopId={chosenShopId}
          onChooseShop={setSelectedShopId}
          chooseShopError={chooseShop.error?.message ?? null}
          onConnectingChange={setConnectingShop}
        />
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-unbox-cream-50">
      <div ref={scrollArea} className="flex-1 overflow-y-auto px-10 pt-8 pb-10">
        <div className="mx-auto flex max-w-[1040px] flex-col gap-8">
          <img src="/unbox-logo-full.svg" alt="Unbox" className="h-7 self-start" />
          <WizardProgress
            current={step}
            warningStep={step === WIZARD_STEPS.length && !connected ? 4 : undefined}
            onSelect={goTo}
            reachable={(n) => n <= furthest}
          />
          <StepHeading title={heading.title} description={heading.description} />
          {stepContent()}
        </div>
      </div>

      <footer className="flex items-center gap-4 border-t border-secondary bg-white px-10 py-4">
        <Button color="secondary" size="md" onClick={onSaveAndExit} isDisabled={isSaving}>
          Save &amp; exit
        </Button>
        <button
          type="button"
          onClick={onDiscard}
          disabled={discardDraft.isPending}
          className="text-sm font-semibold text-gray-700 underline decoration-gray-300 underline-offset-4"
        >
          Discard &amp; exit
        </button>
        <p
          role="status"
          className={cx(
            "flex flex-1 items-center gap-1.5 text-sm",
            message?.isWarning ? "font-semibold text-warning-600" : "text-gray-500",
          )}
        >
          {message?.isWarning && <AlertCircle className="size-4" />}
          {message?.text}
        </p>
        {step > 1 && (
          <Button color="secondary" size="md" iconLeading={ArrowLeft} onClick={() => goTo(step - 1)}>
            Back
          </Button>
        )}
        <Button
          color="primary"
          size="md"
          onClick={onContinue}
          isDisabled={isSaving}
          isLoading={submitDraft.isPending || saveForApproval.isPending}
        >
          {primaryLabel}
        </Button>
      </footer>
    </div>
  );
};
